<template>
  <component
    v-bind="$attrs"
    v-model="internalSelected"
    v-resizable-columns
    :class="['cy-table', tableClasses]"
    :density="density"
    :headers="internalHeaders"
    :items="items"
    :items-length="serverSide ? totalItems : undefined"
    :loading="loading"
    :search="search"
    :page="internalOptions.page"
    :items-per-page="internalOptions.itemsPerPage"
    :sort-by="internalOptions.sortBy"
    :group-by="internalOptions.groupBy"
    fixed-header
    hide-default-footer
    :row-props="rowProps"
    @update:options="handleOptionsUpdate"
    :is="tableComponent">
    <!-- Pass through all slots -->
    <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
      <slot :name="slotName" v-bind="slotProps || {}"/>
    </template>

    <!-- Custom cell rendering (overrides generic slots if they exist) -->
    <template
      v-for="header in genericHeaders"
      :key="header.key"
      #[`item.${header.key}`]="{ item, value }">
      <!-- eslint-disable cy-rules/no-vue2-model -->
      <component
        :header="header"
        :item="item"
        :value="value"
        :is="getCellComponent(header.type)"/>
      <!-- eslint-enable cy-rules/no-vue2-model -->
    </template>

    <template #no-data>
      <slot name="no-data">
        <CyEmptyData
          v-if="_.isEmpty(search)"
          :title="t('noData.title')"
          :text="t('noData.text')"
          variant="default"/>
        <CyEmptyData
          v-else
          icon="mdi:magnify"
          :title="t('emptySearch.title')"
          :text="t('emptySearch.text')"
          variant="default"/>
      </slot>
    </template>
  </component>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { VDataTable, VDataTableServer } from 'vuetify/components'
import { defaultOptionsToApiParams } from '@/utils/dataTableProxy'
import { resolveFromMap } from '@/utils/helpers/headerMap'
import CyEmptyData from './CyEmptyData.vue'

/**
 * @typedef {'success' | 'warning' | 'danger' | 'primary' | 'secondary' | 'accent' | 'muted'} highlightVariant
 */

export default {
  name: 'CyTable',
  components: {
    CyEmptyData,
    VDataTable,
    VDataTableServer,
    CyTableCellText: defineAsyncComponent(() => import('@/components/CyTableCellText.vue')),
    CyTableCellNumber: defineAsyncComponent(() => import('@/components/CyTableCellNumber.vue')),
    CyTableCellBoolean: defineAsyncComponent(() => import('@/components/CyTableCellBoolean.vue')),
    CyTableCellTags: defineAsyncComponent(() => import('@/components/CyTableCellTags.vue')),
    CyTableCellLink: defineAsyncComponent(() => import('@/components/CyTableCellLink.vue')),
  },
  props: {
    serverSide: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Object,
      default: () => ({
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        groupBy: [],
        search: '',
      }),
    },
    highlightRow: {
      /** @type {import('vue').PropType<(item: any, index: number) => string | null>} */
      type: Function,
      default: null,
    },
    density: {
      /** @type {import('vue').PropType<'default' | 'compact'>} */
      type: String,
      default: 'default',
      validator: (value) => ['default', 'compact'].includes(value),
    },
    variant: {
      /** @type {import('vue').PropType<'default' | 'bordered'>} */
      type: String,
      default: 'bordered',
      validator: (value) => ['default', 'bordered'].includes(value),
    },
    headers: {
      type: Array,
      default: () => [],
      validator: (value) => value.every((h) => h.value || h.key),
    },
    itemClass: {
      type: [String, Function],
      default: '',
    },
    search: {
      type: String,
      default: '',
    },
    proxyParamsFn: {
      type: Function,
      default: defaultOptionsToApiParams,
    },
  },
  emits: ['update:modelValue', 'update:options', 'load'],
  data () {
    return {
      internalOptions: { ...this.options },
      hasScrollStart: false,
      hasScrollEnd: false,
      hasScrollTop: false,
      hasScrollBottom: false,
    }
  },
  computed: {
    internalSelected: {
      get () {
        return this.modelValue
      },
      set (val) {
        this.$emit('update:modelValue', val)
      },
    },
    tableComponent () {
      return this.serverSide ? 'v-data-table-server' : 'v-data-table'
    },
    genericHeaders () {
      return this.headers.filter((h) => {
        const slotName = `item.${h.value}`
        return !this.$slots[slotName]
      })
    },
    internalHeaders () {
      // Forward header.title to <th> as a native title attribute so a
      // tooltip is available when the column label is clipped by ellipsis.
      // When header.highlightMap is set, attach a cellProps function so
      // Vuetify can stamp the resolved highlight class onto each <td>.
      return this.headers.map((h) => ({
        ...h,
        headerProps: {
          title: h.title,
          ...h.headerProps,
        },
        cellProps: this.makeCellPropsFn(h),
      }))
    },
    tableClasses () {
      return {
        [`cy-table--${this.variant}`]: true,
        'cy-table--scroll-start': this.hasScrollStart,
        'cy-table--scroll-end': this.hasScrollEnd,
        'cy-table--scroll-top': this.hasScrollTop,
        'cy-table--scroll-bottom': this.hasScrollBottom,
      }
    },
  },
  watch: {
    options: {
      handler (newVal) {
        this.internalOptions = { ...newVal }
      },
      deep: true,
    },
    search (newVal) {
      if (this.internalOptions.search !== newVal) {
        this.handleOptionsUpdate({ search: newVal })
      }
    },
  },
  mounted () {
    this.boundUpdateScrollIndicators = this.updateScrollIndicators.bind(this)
    this.$nextTick().then(() => {
      this.scrollWrapper = this.$el?.querySelector?.('.v-table__wrapper')
      if (!this.scrollWrapper) return
      this.scrollWrapper.addEventListener('scroll', this.boundUpdateScrollIndicators, { passive: true })
      // Observe both the wrapper (clientWidth changes) and the inner
      // table (scrollWidth changes as rows render or columns resize).
      this.scrollResizeObserver = new ResizeObserver(this.boundUpdateScrollIndicators)
      this.scrollResizeObserver.observe(this.scrollWrapper)
      const innerTable = this.scrollWrapper.querySelector('table')
      if (innerTable) this.scrollResizeObserver.observe(innerTable)
      this.boundUpdateScrollIndicators()
    })
  },
  beforeUnmount () {
    this.scrollWrapper?.removeEventListener('scroll', this.boundUpdateScrollIndicators)
    this.scrollResizeObserver?.disconnect()
  },
  methods: {
    updateScrollIndicators () {
      const w = this.scrollWrapper
      if (!w) return
      this.hasScrollStart = w.scrollLeft > 1
      this.hasScrollEnd = w.scrollLeft < w.scrollWidth - w.clientWidth - 1
      this.hasScrollTop = w.scrollTop > 1
      this.hasScrollBottom = w.scrollTop < w.scrollHeight - w.clientHeight - 1
    },
    handleOptionsUpdate (newOptions) {
      // Merge to ensure we don't lose state (e.g. search) if the event payload is partial
      const mergedOptions = { ...this.internalOptions, ...newOptions }

      // If search prop changed, sync it to options
      if (this.search && mergedOptions.search !== this.search) {
        mergedOptions.search = this.search
      }

      this.internalOptions = mergedOptions
      this.$emit('update:options', mergedOptions)

      // Trigger server-side load with proxied params
      if (this.serverSide) {
        const apiParams = this.proxyParamsFn(mergedOptions)
        this.$emit('load', apiParams)
      }
    },
    getCellComponent (type) {
      switch (type) {
        case 'number':
          return 'CyTableCellNumber'
        case 'boolean':
          return 'CyTableCellBoolean'
        case 'tags':
          return 'CyTableCellTags'
        case 'link':
          return 'CyTableCellLink'
        default:
          return 'CyTableCellText'
      }
    },
    makeCellPropsFn (header) {
      // Stable function reference for headers that opt out, so Vuetify
      // doesn't see a new prop each render.
      if (!header.highlightMap) return header.cellProps
      return ({ item, value }) => {
        const color = resolveFromMap(header.highlightMap, value, item)
        const userCellProps = typeof header.cellProps === 'function'
          ? header.cellProps({ item, value })
          : header.cellProps
        if (!color) return userCellProps
        const userClass = userCellProps?.class
        return {
          ...userCellProps,
          class: [userClass, `cy-table-cell--${color}`].filter(Boolean),
        }
      }
    },
    rowProps (data) {
      const { item, index } = data
      const classes = []

      if (this.itemClass) {
        if (typeof this.itemClass === 'function') {
          classes.push(this.itemClass(item, index))
        } else {
          classes.push(this.itemClass)
        }
      }

      if (this.highlightRow) {
        const color = this.highlightRow(item, index)
        if (color) {
          classes.push(`cy-table-row--${color}`)
        }
      }

      return { class: classes.join(' ') }
    },
  },
  setup () {
    const { t } = useI18n()
    return { t }
  },
}
</script>

<style lang="scss" scoped>
$radius: $spacer-3;

.cy-table {
  // Scroll shadows on horizontal overflow. Pseudo-elements are anchored
  // to the table edges and their visibility is toggled by scroll-state
  // classes added by the component on scroll/resize.
  // overflow: hidden clips the rectangular pseudo-elements to the
  // table's rounded corners.
  position: relative;
  overflow: hidden;
  border-radius: $radius;
  box-shadow: none !important;

  :deep(.v-table__wrapper) {
    border-top-left-radius: $radius;
    border-top-right-radius: $radius;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    z-index: 3;
    top: 0;
    bottom: 0;
    width: 14px;
    transition: opacity 200ms ease;
    opacity: 0;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(
      to right,
      cy-get-color("primary", "light-5"),
      cy-get-color("primary", "light-5", $alpha: 0)
    );
  }

  &::after {
    right: 0;
    background: linear-gradient(
      to left,
      cy-get-color("primary", "light-5"),
      cy-get-color("primary", "light-5", $alpha: 0)
    );
  }

  &--scroll-start::before {
    opacity: 1;
  }

  &--scroll-end::after {
    opacity: 1;
  }

  // Vertical scroll shadows. The bottom shadow uses a sticky pseudo
  // inside the scroll wrapper — it pins to the viewport floor without
  // claiming layout space (negative margin) and fades out cleanly.
  :deep(.v-table__wrapper)::after {
    content: "";
    display: block;
    position: sticky;
    z-index: 2;
    right: 0;
    bottom: 0;
    left: 0;
    height: 14px;
    margin-top: -14px;
    transition: opacity 200ms ease;
    opacity: 0;
    background: linear-gradient(
      to top,
      cy-get-color("primary", "light-5"),
      cy-get-color("primary", "light-5", $alpha: 0)
    );
    pointer-events: none;
  }

  &--scroll-bottom :deep(.v-table__wrapper)::after {
    opacity: 1;
  }

  // The top shadow rides along with the sticky thead via box-shadow,
  // so it always lands just below the column labels regardless of
  // density or thead height.
  &--scroll-top :deep(thead th) {
    box-shadow: 0 14px 14px -14px cy-get-color("primary", "light-5");
  }

  &--bordered {
    border: 1px solid cy-get-color("primary", "light-4");
  }

  &--default {
    border: none !important;
  }

  :deep(thead) {
    th {
      overflow: hidden;

      .v-data-table-header__content {
        overflow: hidden;
        gap: 4px;
        color: cy-get-color("primary", "light-2");

        // Truncate the label with ellipsis when the column is too narrow.
        // The native title attribute on <th> surfaces the full label.
        > span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .v-icon {
          opacity: 1;
          color: inherit !important;
        }
      }

      &:hover {
        background-color: cy-get-color("primary", "light-5") !important;

        .v-data-table-header__content {
          color: cy-get-color("primary", "main");

          .v-icon {
            opacity: 1;
            color: inherit !important;
          }
        }
      }
    }
  }

  :deep(tbody) {
    tr {
      // Translucent overlay so hover stays visible over any highlight
      // bg. We paint it on each <td> instead of the <tr> because cell-
      // level highlights set an opaque bg-color on td, which would
      // otherwise cover a tr-level overlay.
      &:hover > td {
        background-image: linear-gradient(
          cy-get-color("primary", "light-5", $alpha: 0.5),
          cy-get-color("primary", "light-5", $alpha: 0.5)
        );
      }

      &.cy-table-row--success {
        background-color: cy-get-color("success", "light-2");
      }

      &.cy-table-row--warning {
        background-color: cy-get-color("warning", "light-2");
      }

      &.cy-table-row--danger {
        background-color: cy-get-color("error", "light-2");
      }

      &.cy-table-row--primary {
        background-color: cy-get-color("primary", "light-5");
      }

      &.cy-table-row--secondary {
        background-color: cy-get-color("secondary", "light-3");
      }

      &.cy-table-row--accent {
        background-color: cy-get-color("accent", "light-4");
      }

      &.cy-table-row--muted {
        background-color: cy-get-color("grey", "light-2");
      }
    }

    // Cell-level highlights. Opaque so they override any row highlight
    // on the same row, mirroring the Figma "Highlight individual cells"
    // pattern.
    td {
      &.cy-table-cell--success {
        background-color: cy-get-color("success", "light-2");
      }

      &.cy-table-cell--warning {
        background-color: cy-get-color("warning", "light-2");
      }

      &.cy-table-cell--danger {
        background-color: cy-get-color("error", "light-2");
      }

      &.cy-table-cell--primary {
        background-color: cy-get-color("primary", "light-5");
      }

      &.cy-table-cell--secondary {
        background-color: cy-get-color("secondary", "light-3");
      }

      &.cy-table-cell--accent {
        background-color: cy-get-color("accent", "light-4");
      }

      &.cy-table-cell--muted {
        background-color: cy-get-color("grey", "light-2");
      }
    }
  }
}
</style>

<i18n lang="json">
{
  "en": {
    "noData": {
      "title": "No data available",
      "text": "This table does not contain any entries yet."
    },
    "emptySearch": {
      "title": "No results found",
      "text": "No results found for the search query."
    }
  },
  "fr": {
    "noData": {
      "title": "Aucune donnée disponible",
      "text": "Cette table ne contient aucune entrée encore."
    },
    "emptySearch": {
      "title": "Aucun résultat trouvé",
      "text": "Aucun résultat trouvé pour la requête de recherche."
    }
  },
  "es": {
    "noData": {
      "title": "No hay datos disponibles",
      "text": "Esta tabla no contiene ninguna entrada todavía."
    },
    "emptySearch": {
      "title": "No se encontraron resultados",
      "text": "No se encontraron resultados para la consulta de búsqueda."
    }
  }
}
</i18n>
